import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { asRoleCode, derivePermissions, type Permissions } from '@/access';
import { useRolesQuery, useUserQuery } from '@/api/queries/useAuth';
import type { userRole } from '@/enums';
import { useCompanyId, useProjectId } from '@/hooks';

/**
 * What the signed-in user may do in the current workspace (company + project),
 * as booleans — see `@/access/permissions` for the rules. Screens read these
 * instead of comparing role codes; the backend remains the authority and the
 * predicates only decide what to show.
 */
export function usePermissions(): Permissions {
    const { data: user } = useUserQuery();
    const { data: roles } = useRolesQuery();
    const companyId = useCompanyId();
    const projectId = useProjectId();
    return useMemo(() => derivePermissions({ user, roles, companyId, projectId }), [user, roles, companyId, projectId]);
}

/**
 * The role dictionary (`GET roles`) — id ⇄ code ⇄ localized name — for the
 * role pickers and the role chips, which speak in `role_id`s. Codes are
 * normalised through `asRoleCode`, so a backend still answering a legacy
 * code (`project_producer`, `employee`…) reads under its current name.
 */
export function useRoles() {
    const { t } = useTranslation();
    const { data: roles } = useRolesQuery();

    return useMemo(() => {
        const all = roles ? [...roles.company, ...roles.project] : [];
        const getRoleCode = (id: number | null | undefined): userRole | undefined =>
            asRoleCode(all.find(r => r.id === id)?.code) ?? undefined;
        const getRoleID = (code: userRole | string): number => {
            const wanted = asRoleCode(code);
            return all.find(r => asRoleCode(r.code) === wanted)?.id ?? 0;
        };
        const getRoleName = (id: number | null | undefined): string => {
            const code = getRoleCode(id);
            return code ? t(`roles.${code}`) : '';
        };
        return { roles, isLoaded: roles !== undefined, getRoleCode, getRoleID, getRoleName };
    }, [roles, t]);
}
